import { Menu, Popover, Typography } from 'antd';
import { compact, every, isNumber, property, range, values } from 'lodash-es';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useHistory } from 'react-router';

import { useMountAndUpdateEffect } from '../../common/utils/hookUtils';
import GoogleMapAdapter from '../../components/adapters/GoogleMapAdapter';
import { FAIconChars } from '../../components/adapters/fontAwesomeAdapters';
import {
  iconLabel,
  imgIcon,
} from '../../components/adapters/googleMap/googleMapElements';
import { makeMarker } from '../../components/adapters/googleMap/googleMapHelpers';
import {
  Scrollbars,
  VerticalTrackTypes,
} from '../../components/layout/Scrollbars';
import { useScrollbarsStyles } from '../../components/layout/Scrollbars.styles';
import { MapCard } from '../../components/maps/MapCard';
import iconBlue from '../../resources/img/icons/map-circle-blue.png';
import iconGrey from '../../resources/img/icons/map-circle-grey.png';
import iconYellow from '../../resources/img/icons/map-circle-yellow.png';
import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { latLng, mapSize } from '../../utils/mapUtils';
import MonitorJobMapLegend from './MonitorJobMapLegend';
import { MonitorContext, getLocationModeInfo } from './monitorContexts';
import { ActivityIndicators } from './MonitorJobList';
import routes from '../../app/routes';
import { ShipmentStage } from '../../app/enums/ShipmentStage';
import { useMonitorJobMapStyles } from './MonitorJobMap.styles';

function nonDeliveredMarker() {
  return {
    icon: imgIcon(iconBlue, mapSize(40, 40)),
  };
}
function delayedMarker() {
  return {
    icon: imgIcon(iconYellow, mapSize(40, 40)),
    label: iconLabel(
      FAIconChars.EXCLAMATION,
      cssVariables.colorWhite,
      cssVariables['font-size-sm-minus']
    ),
    tooltipContent: <FormattedMessage id="monitor.map.marker.revisedEta" />,
  };
}
function deliveredMarker() {
  return {
    icon: imgIcon(iconGrey, mapSize(40, 40)),
    label: iconLabel(
      FAIconChars.CHECK,
      cssVariables.colorWhite,
      cssVariables['font-size-sm-minus']
    ),
    tooltipContent: <FormattedMessage id="monitor.map.marker.delivered" />,
  };
}
function jobMarker({ serviceUpdate, deliveryDateTime }) {
  if (deliveryDateTime?.actual) {
    return deliveredMarker();
  }
  if (serviceUpdate) {
    return delayedMarker();
  }
  return nonDeliveredMarker();
}

function useMarkers() {
  const { data, activeSection } = useContext(MonitorContext);
  const firstLoadDone = every(
    compact(values(data)).map(grp => grp.rows.length > 0 || !grp.loading)
  );
  const expandedJobs = data[activeSection]?.rows;

  const { locationMode, setLocationMode, ...activeJobCtrl } =
    useContext(MonitorContext);
  const activeJobCtrlRef = useRef(activeJobCtrl);
  useEffect(() => {
    activeJobCtrlRef.current = activeJobCtrl;
  });

  const markers = useMemo(
    () =>
      expandedJobs
        ?.map(job => {
          const { getJobLocation } = getLocationModeInfo(locationMode);
          return {
            location: getJobLocation(job)?.location,
            jobNumber: job.jobNumber || job.referenceNo,
            serviceUpdate: job.serviceUpdate,
            deliveryDateTime: job.deliveryDateTime,
            indicators: job.indicators,
            shipper: job.origin?.companyName,
            consignee: job.destination?.companyName,
            stage: job?.stage,
            quoteStatus: job?.quoteStatus,
          };
        })
        .filter(property('location'))
        .map(
          ({
            location,
            jobNumber,
            serviceUpdate,
            deliveryDateTime,
            indicators,
            shipper,
            consignee,
            stage,
            quoteStatus,
          }) => {
            const { lat, lng } = location;

            return makeMarker(latLng(lat, lng), {
              ...jobMarker({ serviceUpdate, deliveryDateTime }),
              data: {
                indicators,
                jobNumber,
                shipper,
                consignee,
                stage,
                quoteStatus,
              },
              shape: {
                type: 'circle',
                coords: [20, 20, 10],
              },
              onMouseOver: () => {
                const { activeJob, setActiveJob } = activeJobCtrlRef.current;
                if (activeJob !== jobNumber) {
                  setActiveJob(jobNumber);
                }
              },
            });
          }
        ),
    [expandedJobs, locationMode]
  );

  return { markers, firstLoadDone };
}

function ClusterTooltipContentWrapper({
  maxCount,
  maxHeight,
  jobsData,
  open,
  setOpen,
}) {
  const { styles } = useMonitorJobMapStyles();
  const [size, setSize] = useState();
  const { push } = useHistory();
  const { setActiveJob } = useContext(MonitorContext);
  const count = jobsData.length;
  const menuRef = useRef(null);

  useMountAndUpdateEffect(
    {
      onUpdate: () => {
        setSize(undefined);
      },
    },
    [count]
  );

  useEffect(() => {
    const el = menuRef.current;
    let ro = null;

    if (open && el) {
      ro = new ResizeObserver(entries => {
        const entry = entries[0];

        if (!entry) return;

        const { width, height } = entry.contentRect;

        if (width > 0 && height > 0) {
          setSize({
            width,
            height: Math.min(height, (height / count) * maxCount),
          });
        }
      });

      ro.observe(el);
    }

    return () => {
      ro && ro.disconnect();
    };
  }, [open, count, maxCount]);

  const useScrollbar =
    count > maxCount ||
    (isNumber(maxHeight) && size && size.height > maxHeight);
  const items = jobsData.map(
    ({ indicators, jobNumber, shipper, consignee, stage, quoteStatus }) => ({
      key: jobNumber,
      onMouseOver: () => setActiveJob(jobNumber),
      onClick: () => {
        setOpen(false);
        if (stage === ShipmentStage.SAVED) {
          push(generatePath(routes.releaseShipment, { jobNumber }));
        } else if (quoteStatus === 'QUOTE') {
          push(
            generatePath(routes.quoting.quoteDetail, { referenceNo: jobNumber })
          );
        } else {
          push(generatePath(routes.shipmentDetail, { jobNumber }));
        }
      },
      label: (
        <span className="map-dropdown-menu-overlay-item">
          <span>{jobNumber}</span>
          <span>|</span>
          <Typography.Text
            className="menu-overlay-item-limited-chars"
            ellipsis
            title={shipper}
          >
            {shipper}
          </Typography.Text>
          <span>/</span>
          <Typography.Text
            className="menu-overlay-item-limited-chars"
            ellipsis
            title={consignee}
          >
            {consignee}
          </Typography.Text>
          <ActivityIndicators
            customGPSIconSize={18}
            indicators={indicators}
            showNaLabel={false}
            indicatorClassName={styles.activityIndicator}
          />
        </span>
      ),
    })
  );

  return useScrollbar && size ? (
    <Scrollbars
      vertical={VerticalTrackTypes.ABOVE_CONTENT}
      style={{
        width: size.width,
        height: Math.min(size.height, ...[maxHeight].filter(isNumber)),
      }}
    >
      <Menu selectedKeys={[]} items={items} />
    </Scrollbars>
  ) : (
    <div ref={menuRef}>
      <Menu selectedKeys={[]} items={items} />
    </div>
  );
}

function ClusterTooltip({ jobsData, rect, rectElement }) {
  const [open, setOpen] = useState(false);
  const { styles: scrollbarsStyles } = useScrollbarsStyles();
  const { styles } = useMonitorJobMapStyles(scrollbarsStyles);
  const topSpace = rect.top;
  const bottomSpace = document.body.clientHeight - rect.bottom;
  const maxHeight =
    Math.max(topSpace, bottomSpace) - pxToNumber(cssVariables.spaceNorm2);

  return (
    <Popover
      classNames={{ root: styles.mapDropdownMenuOverlay }}
      placement={topSpace >= bottomSpace ? 'top' : 'bottom'}
      autoAdjustOverflow={false}
      trigger="hover"
      content={
        <ClusterTooltipContentWrapper
          open={open}
          maxCount={15}
          maxHeight={maxHeight}
          jobsData={jobsData}
          setOpen={setOpen}
        />
      }
      onOpenChange={setOpen}
      open={open}
    >
      {rectElement}
    </Popover>
  );
}

const CLUSTERER_STYLES = range(0, 5).map(() => ({
  url: iconBlue,
  width: 40,
  height: 40,
  textColor: cssVariables.colorWhite,
  textSize: pxToNumber(cssVariables['font-size-base']),
  fontFamily: cssVariables.fontStd,
  fontWeight: 700,
}));

function getClusterTooltip({ markers: ms, rect, rectElement }) {
  return (
    <ClusterTooltip
      key={ms.map(m => m?.data?.jobNumber).join(',')}
      jobsData={ms.map(m => m?.data)}
      rect={rect}
      rectElement={rectElement}
    />
  );
}
const CLUSTERER_PROPS = {
  enable: true,
  getTooltip: getClusterTooltip,
  styles: CLUSTERER_STYLES,
};

const defaultRenderMap = map => <MapCard>{map}</MapCard>;
export default function MonitorJobMap({ renderMap = defaultRenderMap }) {
  const { markers, firstLoadDone } = useMarkers();

  return renderMap(
    <GoogleMapAdapter
      markers={markers}
      // This is to set bounds only after all the groups were loaded
      markersReady={firstLoadDone}
      clustererProps={CLUSTERER_PROPS}
    >
      <MonitorJobMapLegend />
    </GoogleMapAdapter>
  );
}
